import React, { useMemo, useState } from "react";
import { Row, Col, Form, Select, Input, Button, Collapse, DatePicker } from "antd";
import type { FormInstance } from "antd";
import { useAppSelector } from "../../..";
import type { AnyObject, DictionaryDto } from "../../../lib/interfaces";
import { checkIsLoaded, checkIsLoading, displayFormat } from "../../../lib/utils";
import MultipleSelect from "../../common/MultipleSelect";
import { IClientType, ISearchFilterField } from "../../../lib/types";
import { BulkFiltersToggler } from "../../BulkFiltersToggler/BulkFiltersToggler";
import { ActiveSearchFormItem, AgreementsSearchFormItem } from "../../BulkSearchItems";
import "./BulkServicesSearchFilters.less";

const layout = {
  labelCol: { span: 8 },
  wrapperCol: { span: 16 },
};

interface ComponentProps {
  form: FormInstance;
  defaultServicesSearchParams: AnyObject;
  onSearch: (values: AnyObject) => void;
  resetSearchPaymentsAndResetSetSelectedRowKeys: () => void;
  filteredServiceStatuses: DictionaryDto[];
  filtersCollapseState: string;
  setFiltersCollapsState: React.Dispatch<React.SetStateAction<string>>;
}

export const BulkServicesSearchFilters = (props: ComponentProps) => {
  const {
    filteredServiceStatuses,
    resetSearchPaymentsAndResetSetSelectedRowKeys,
    filtersCollapseState,
    form,
    defaultServicesSearchParams,
    onSearch,
    setFiltersCollapsState,
  } = props;

  const { serviceStatusLoading } = useAppSelector((state) => state.rtkDictionaries);

  const { tenant } = useAppSelector((state) => state.tenants);

  const getPanelHeader = () =>
    filtersCollapseState === "unfolded" ? (
      ""
    ) : (
      <div className="bulk__panel_header">Показать фильтры</div>
    );

  const { servicesSearchingLoading } = useAppSelector((state) => state.service);

  const serviceTypes = useAppSelector((state) => state.service.serviceTypes) as DictionaryDto[];

  const [clientType, setClientType] = useState<IClientType>("ORGANIZATION");

  const toggleFiltersCollapseState = () => {
    const newState = filtersCollapseState === "unfolded" ? "folded" : "unfolded";
    setFiltersCollapsState(newState);
  };

  const allFieldProducts: ISearchFilterField[] = useMemo(
    () => [
      {
        label: "Клиент",
        fieldElement: (
          <AgreementsSearchFormItem
            name="accountIds"
            multiselect
            combineAgreements
            clientType={clientType}
            setClientType={setClientType}
            onChange={() => {
              form.setFieldsValue({
                "organization.property.stringValue": undefined,
                "subscriber.externalId": undefined,
              });
            }}
          />
        ),
        visible: true,
      },
      {
        label: "Статус продукта",
        fieldElement: (
          <Form.Item label="Статус продукта" name="serviceStatusId">
            <MultipleSelect
              placeholder="Выбрать"
              showArrow
              allValues={filteredServiceStatuses?.map(({ id }) => id) || []}
              maxTagCount={3}
              loading={checkIsLoading(serviceStatusLoading)}
              allowClear
            >
              {checkIsLoaded(serviceStatusLoading) &&
                filteredServiceStatuses &&
                filteredServiceStatuses.map(({ id, name }) => (
                  <Select.Option value={id} key={id}>
                    {name}
                  </Select.Option>
                ))}
            </MultipleSelect>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "ID продуктов",
        fieldElement: (
          <Form.Item
            label="ID продуктов"
            name="serviceIds"
            className="form-item-required__hidden-error__mb0"
            rules={[
              {
                validator: (x, value) => {
                  if (value && /[^0-9,]/.test(value)) {
                    return Promise.reject();
                  }
                  return Promise.resolve();
                },
                message: "В качестве разделителя может использоваться только запятая",
              },
            ]}
          >
            <Input placeholder={`Введите ID продуктов через ","`} allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: tenant?.id === 1 ? "БИН" : "ИНН",
        fieldElement: (
          <ActiveSearchFormItem
            name="organization.property.stringValue"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "subscriber.externalId": undefined,
              });
            }}
            clientType={clientType}
            minLengthSearch={8}
          />
        ),
        visible: false,
      },
      {
        label: "Тех. ID",
        labelTitle: "Технический ID",
        fieldElement: (
          <ActiveSearchFormItem
            name="subscriber.externalId"
            onSelect={(props) => {
              form.setFieldsValue({
                accountIds: [props.accountId],
                "organization.property.stringValue": undefined,
              });
            }}
            clientType={clientType}
            minLengthSearch={3}
          />
        ),
        visible: true,
      },
      {
        label: "Тип продукта",
        fieldElement: (
          <Form.Item label="Тип продукта" name="serviceTypeId">
            <Select
              showSearch
              placeholder="Выбрать"
              showArrow
              mode="multiple"
              allowClear
              filterOption={(input, option) =>
                // @ts-ignore
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {serviceTypes &&
                serviceTypes.length !== 0 &&
                serviceTypes.map((item) => (
                  <Select.Option value={item.id} key={item.id}>
                    {item.name}
                  </Select.Option>
                ))}
            </Select>
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Код предложения",
        fieldElement: (
          <Form.Item label="Код предложения" name="offeringName">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Наименование предложения",
        fieldElement: (
          <Form.Item label="Наименование" name="offeringExtName">
            <Input allowClear />
          </Form.Item>
        ),
        visible: true,
      },
      {
        label: "Дата",
        fieldElement: (
          <Form.Item label="Дата" name="onDate">
            <DatePicker format={displayFormat} style={{ width: "100%" }} />
          </Form.Item>
        ),
        visible: false,
      },
    ],
    [filteredServiceStatuses, clientType, serviceTypes]
  );

  const [fields, setFields] = useState<ISearchFilterField[]>(allFieldProducts);

  return (
    <div className="bulkServices-searchFilters">
      <div className="inner-row">
        <Collapse
          defaultActiveKey="1"
          onChange={toggleFiltersCollapseState}
          expandIconPosition="right"
          className="filter-collapse"
        >
          <Collapse.Panel header={getPanelHeader()} key="1" className="bulk__panel">
            <Row justify="end">
              <BulkFiltersToggler
                allFields={allFieldProducts}
                setFields={setFields}
                filterName="bulk-products"
              />
            </Row>
            <Form
              form={form}
              labelAlign="left"
              initialValues={defaultServicesSearchParams}
              {...layout}
              onFinish={onSearch}
            >
              <Row gutter={50}>
                {fields
                  .filter(({ visible }) => visible)
                  .map(({ fieldElement }, idx) => (
                    <Col span={8} key={idx}>
                      {fieldElement}
                    </Col>
                  ))}
              </Row>
              <Form.Item wrapperCol={{ span: 24 }} style={{ textAlign: "right", marginBottom: 0 }}>
                <Button onClick={resetSearchPaymentsAndResetSetSelectedRowKeys}>
                  Сброс параметров
                </Button>
                <Button
                  loading={checkIsLoading(servicesSearchingLoading)}
                  htmlType="submit"
                  type="primary"
                >
                  Применить
                </Button>
              </Form.Item>
            </Form>
          </Collapse.Panel>
        </Collapse>
      </div>
    </div>
  );
};
